import React, { useEffect, useRef, useState } from 'react';
import './ImpactNumbers.css';

const stats = [
  {
    number: 200,
    suffix: '+',
    label: 'Projects Delivered',
    description: 'Successfully completed projects across diverse industries',
  },
  {
    number: 98,
    suffix: '%',
    label: 'Client Satisfaction',
    description: 'Clients rate our services as excellent or outstanding',
  },
  {
    number: 50,
    suffix: '+',
    label: 'Tech Experts',
    description: 'Certified professionals ready to serve you',
  },
  {
    number: 15,
    suffix: '+',
    label: 'Years Experience',
    description: 'Combined expertise in technology solutions',
  },
  {
    number: 99.9,
    suffix: '%',
    label: 'System Uptime',
    description: 'Reliability you can count on, always',
  },
  {
    number: 24,
    suffix: '/7',
    label: 'Support Available',
    description: 'Round-the-clock assistance whenever you need',
  },
];

function CountUpAnimation({ end, duration, suffix }: { end: number; duration: number; suffix: string }) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };

    window.requestAnimationFrame(step);
  }, [isVisible, end, duration]);

  return (
    <div ref={elementRef} className="stat-number">
      {count}
      {suffix}
    </div>
  );
}

export function ImpactNumbers() {
  return (
    <section className="impact-numbers section">
      <div className="impact-background">
        <div className="impact-pattern"></div>
      </div>

      <div className="impact-content">
        <div className="impact-header">
          <h2 className="section-title impact-title">Our Impact in Numbers</h2>
          <p className="section-subtitle">
            Real metrics that showcase our commitment to excellence and innovation.
          </p>
        </div>

        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="stat-card"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="stat-card-inner">
                <CountUpAnimation end={stat.number} duration={2000} suffix={stat.suffix} />
                <div className="stat-label">{stat.label}</div>
                <p className="stat-description">{stat.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
